// HEADY_BRAND:BEGIN
// ╔══════════════════════════════════════════════════════════════════╗
// ║  ██╗  ██╗███████╗ █████╗ ██████╗ ██╗   ██╗                     ║
// ║  ██║  ██║██╔════╝██╔══██╗██╔══██╗╚██╗ ██╔╝                     ║
// ║  ███████║█████╗  ███████║██║  ██║ ╚████╔╝                      ║
// ║  ██╔══██║██╔══╝  ██╔══██║██║  ██║  ╚██╔╝                       ║
// ║  ██║  ██║███████╗██║  ██║██████╔╝   ██║                        ║
// ║  ╚═╝  ╚═╝╚══════╝╚═╝  ╚═╝╚═════╝    ╚═╝                        ║
// ║                                                                  ║
// ║  ∞ SACRED GEOMETRY ∞  Organic Systems · Breathing Interfaces    ║
// ║  ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━  ║
// ║  FILE: HeadyAI-IDE/src/App.jsx                                   ║
// ║  LAYER: frontend/src                                             ║
// ╚══════════════════════════════════════════════════════════════════╝
// HEADY_BRAND:END

import React, { useEffect, useCallback, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// State management
import { IDEProvider, useIDE, useIDEActions, VIEWS, PANELS } from './stores/ideStore';

// Components
import ActivityBar from './components/ActivityBar';
import TabBar from './components/TabBar';
import Breadcrumbs from './components/Breadcrumbs';
import Editor from './components/Editor';
import FileExplorer from './components/FileExplorer';
import AIChat from './components/AIChat';
import Terminal from './components/Terminal';
import SearchPanel from './components/SearchPanel';
import GitPanel from './components/GitPanel';
import CommandPalette from './components/CommandPalette';
import ModelSelector from './components/ModelSelector';
import SacredGeometryBackground from './components/SacredGeometryBackground';
import GovernanceDashboard from './components/GovernanceDashboard';

// Cloud service
import { useCloudConnection, useKeyboardShortcuts } from './hooks/useCloudConnection';
import fileSystemService from './services/FileSystemService';

const IDELayout = () => {
  const { state } = useIDE();
  const actions = useIDEActions();
  const cloud = useCloudConnection();
  useKeyboardShortcuts();
  const [fileTree, setFileTree] = useState(null);

  const {
    activeView, sidebarOpen, openTabs, activeTabId,
    bottomPanelOpen, activeBottomPanel, bottomPanelHeight,
    commandPaletteOpen, aiChatOpen, selectedModel,
    connectionStatus, gitBranch, problems, outputLines,
  } = state;

  const activeTab = openTabs.find(t => t.id === activeTabId);

  // Handle file selection — load content into a tab
  const handleFileSelect = useCallback(async (file) => {
    if (file.isDirectory) return;
    const content = await fileSystemService.readFile(file.path);
    const lang = file.name.split('.').pop() || 'text';
    actions.openTab({
      id: file.path,
      name: file.name,
      path: file.path,
      content,
      language: lang,
      isDirty: false,
    });
  }, [actions]);

  // Handle Open Folder
  const handleOpenFolder = useCallback(async (refresh = false) => {
    const tree = await fileSystemService.openFolder(refresh === true || refresh?.type === 'click');
    if (tree) setFileTree(tree);
  }, []);

  // Load file tree on mount and when connection status changes
  useEffect(() => {
    handleOpenFolder();
  }, [connectionStatus, handleOpenFolder]);

  const handleCreateFile = useCallback(async () => {
    if (!fileTree) return;
    const fileName = prompt('Enter file name:');
    if (!fileName) return;

    let targetDir = fileTree.path;
    if (activeTab) {
      const parts = activeTab.path.split(/[/\\]/);
      parts.pop(); // Remove file name
      targetDir = parts.join('/');
    }

    const fullPath = targetDir.endsWith('/') || targetDir.endsWith('\\')
      ? `${targetDir}${fileName}`
      : `${targetDir}/${fileName}`;

    const success = await fileSystemService.createFile(fullPath, '');
    if (success) {
      const updatedTree = await fileSystemService.openFolder(true);
      if (updatedTree) setFileTree(updatedTree);
      
      handleFileSelect({
        name: fileName,
        path: fullPath,
        isDirectory: false
      });
    }
  }, [fileTree, activeTab, handleFileSelect]);

  const handleCreateFolder = useCallback(async () => {
    if (!fileTree) return;
    const folderName = prompt('Enter folder name:');
    if (!folderName) return;

    let targetDir = fileTree.path;
    if (activeTab) {
      const parts = activeTab.path.split(/[/\\]/);
      parts.pop(); // Remove file name
      targetDir = parts.join('/');
    }

    const fullPath = targetDir.endsWith('/') || targetDir.endsWith('\\')
      ? `${targetDir}${folderName}`
      : `${targetDir}/${folderName}`;

    const success = await fileSystemService.createDirectory(fullPath);
    if (success) {
      const updatedTree = await fileSystemService.openFolder(true);
      if (updatedTree) setFileTree(updatedTree);
    }
  }, [fileTree, activeTab]);

  // xterm.js command handler
  const handleTerminalCommand = useCallback((cmd, term) => {
    const trimmed = cmd.trim();
    if (!trimmed) return;

    if (trimmed === 'help') {
      term.writeln('Available commands: help, clear, ls, pwd, echo, whoami, date, neofetch, ops');
    } else if (trimmed === 'clear') {
      term.clear();
    } else if (trimmed === 'ls') {
      if (fileTree?.children) {
        const names = fileTree.children.map(c => (c.isDirectory ? `\x1b[34m${c.name}/\x1b[0m` : c.name));
        term.writeln(names.join('  '));
      }
    } else if (trimmed === 'pwd') {
      term.writeln(fileTree?.path || '/heady-project');
    } else if (trimmed.startsWith('echo ')) {
      term.writeln(trimmed.slice(5));
    } else if (trimmed === 'whoami') {
      term.writeln('heady@headysystems.com (HeadyAI-IDE)');
    } else if (trimmed === 'date') {
      term.writeln(new Date().toString());
    } else if (trimmed === 'neofetch') {
      term.writeln('  ██╗  ██╗███████╗ █████╗ ██████╗ ██╗   ██╗');
      term.writeln('  ██║  ██║██╔════╝██╔══██╗██╔══██╗╚██╗ ██╔╝');
      term.writeln('  ███████║█████╗  ███████║██║  ██║ ╚████╔╝ ');
      term.writeln('  ██╔══██║██╔══╝  ██╔══██║██║  ██║  ╚██╔╝  ');
      term.writeln('  ██║  ██║███████╗██║  ██║██████╔╝   ██║   ');
      term.writeln('  ╚═╝  ╚═╝╚══════╝╚═╝  ╚═╝╚═════╝    ╚═╝   ');
      term.writeln('');
      term.writeln('  OS: HeadyOS v5.0.0 (Liquid Architecture)');
      term.writeln('  Kernel: Latent Space v9.0');
      term.writeln('  Shell: HeadyAI-IDE Terminal v1.0');
      term.writeln(`  φ: ${(1 + Math.sqrt(5)) / 2}`);
    } else if (trimmed === 'ops') {
      term.writeln('\x1b[36m[SYS] Initiating System Ops Data Projection...\x1b[0m');
      fileSystemService.readFile('/home/headyme/Heady/src/sites/admin/health.json').then(content => {
        if (content) {
          try {
            const data = JSON.parse(content);
            const score = ((data.healthy / data.total) * 100).toFixed(1);
            term.writeln(`\x1b[1mSystem Health:\x1b[0m \x1b[32m${data.healthy}\x1b[0m / ${data.total} services`);
            term.writeln(`\x1b[1mPipeline Score:\x1b[0m ${score}%`);
            const down = data.total - data.healthy;
            if (down > 0) term.writeln(`\x1b[33mWarning: ${down} services require attention.\x1b[0m`);
          } catch(e) {
            term.writeln('\x1b[31m[SYS] Error parsing ops data.\x1b[0m');
          }
        } else {
           term.writeln('\x1b[33m[SYS] Ops data not available locally. Run pipeline to generate.\x1b[0m');
        }
      }).catch(() => term.writeln('\x1b[31m[SYS] Failed to read ops data.\x1b[0m'));
    } else {
      term.writeln(`\x1b[31mheady: command not found: ${trimmed}\x1b[0m`);
      term.writeln('\x1b[3mType "help" for available commands\x1b[0m');
    }
  }, [fileTree]);

  // Keyboard shortcuts event handlers
  useEffect(() => {
    const handleSave = () => {
      if (activeTab && activeTab.isDirty) {
        fileSystemService.writeFile(activeTab.path, activeTab.content).then(() => {
          actions.markTabSaved(activeTab.id);
        });
      }
    };

    const handleCloseTab = () => {
      if (activeTabId) {
        actions.closeTab(activeTabId);
      }
    };

    window.addEventListener('heady:save', handleSave);
    window.addEventListener('heady:close-tab', handleCloseTab);

    return () => {
      window.removeEventListener('heady:save', handleSave);
      window.removeEventListener('heady:close-tab', handleCloseTab);
    };
  }, [actions, activeTab, activeTabId]);

  // Render sidebar content based on active view
  const renderSidebarContent = () => {
    switch (activeView) {
      case VIEWS.EXPLORER:
        return <FileExplorer fileTree={fileTree} onFileSelect={handleFileSelect} activeFile={activeTab} onOpenFolder={handleOpenFolder} onRefresh={handleOpenFolder} onCreateFile={handleCreateFile} onCreateFolder={handleCreateFolder} />;
      case VIEWS.SEARCH:
        return <SearchPanel />;
      case VIEWS.GIT:
        return <GitPanel />;
      case VIEWS.AI:
        return <AIChat model={selectedModel} activeFile={activeTab} onClose={() => actions.setActiveView(VIEWS.EXPLORER)} />;
      case VIEWS.EXTENSIONS:
        return (
          <div className="panel-content">
            <h3 className="panel-title">Extensions</h3>
            <div className="extensions-search">
              <input type="text" placeholder="Search extensions..." className="search-input" />
            </div>
            <div className="extensions-list">
              {[
                { name: 'Heady Python', desc: 'Python language support', installed: true },
                { name: 'Heady TypeScript', desc: 'TypeScript tooling', installed: true },
                { name: 'Sacred Themes', desc: 'Sacred Geometry color themes', installed: true },
                { name: 'Heady Rust', desc: 'Rust analyzer integration', installed: false },
                { name: 'Heady Docker', desc: 'Docker & container support', installed: false },
                { name: 'Heady K8s', desc: 'Kubernetes manifests', installed: false },
              ].map((ext, i) => (
                <div key={i} className="extension-item">
                  <div className="ext-info">
                    <span className="ext-name">{ext.name}</span>
                    <span className="ext-desc">{ext.desc}</span>
                  </div>
                  <button className={`ext-btn ${ext.installed ? 'installed' : ''}`}>
                    {ext.installed ? 'Installed' : 'Install'}
                  </button>
                </div>
              ))}
            </div>
          </div>
        );
      case VIEWS.DEBUG:
        return (
          <div className="panel-content">
            <h3 className="panel-title">Run & Debug</h3>
            <button className="debug-start-btn">Start Debugging (F5)</button>
            <div className="debug-section">
              <h4>Breakpoints</h4>
              <div className="debug-empty">No breakpoints set</div>
            </div>
            <div className="debug-section">
              <h4>Watch</h4>
              <div className="debug-empty">Add watch expressions</div>
            </div>
            <div className="debug-section">
              <h4>Call Stack</h4>
              <div className="debug-empty">Not running</div>
            </div>
          </div>
        );
      case VIEWS.SETTINGS:
        return (
          <div className="panel-content">
            <h3 className="panel-title">Settings</h3>
            <div className="settings-list">
              <div className="setting-item">
                <label>Font Size</label>
                <input type="number" defaultValue={13} min={8} max={34}
                  onChange={(e) => actions.updateEditorSettings({ fontSize: parseInt(e.target.value) })} />
              </div>
              <div className="setting-item">
                <label>Tab Size</label>
                <select defaultValue="2" onChange={(e) => actions.updateEditorSettings({ tabSize: parseInt(e.target.value) })}>
                  <option value="2">2 spaces</option>
                  <option value="4">4 spaces</option>
                </select>
              </div>
              <div className="setting-item">
                <label>Minimap</label>
                <input type="checkbox" defaultChecked onChange={(e) => actions.updateEditorSettings({ minimap: e.target.checked })} />
              </div>
              <div className="setting-item">
                <label>Word Wrap</label>
                <select defaultValue="off" onChange={(e) => actions.updateEditorSettings({ wordWrap: e.target.value })}>
                  <option value="off">Off</option>
                  <option value="on">On</option>
                  <option value="wordWrapColumn">Column</option>
                </select>
              </div>
              <div className="setting-item">
                <label>AI Model</label>
                <ModelSelector selectedModel={selectedModel} onModelChange={(m) => actions.setSelectedModel(m)} />
              </div>
            </div>
          </div>
        );
      case VIEWS.GOVERNANCE:
        return <GovernanceDashboard />;
      default:
        return null;
    }
  };

  return (
    <div className="ide-container">
      <SacredGeometryBackground />

      {/* Command Palette Overlay */}
      <AnimatePresence>
        {commandPaletteOpen && <CommandPalette />}
      </AnimatePresence>

      <div className="ide-layout">
        {/* Activity Bar (leftmost icons) */}
        <ActivityBar />

        {/* Sidebar */}
        <AnimatePresence>
          {sidebarOpen && (
            <motion.div
              className="ide-sidebar"
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 280, opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              {renderSidebarContent()}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main Content */}
        <div className="ide-main">
          {/* Tab Bar */}
          <TabBar />

          {/* Breadcrumbs */}
          {activeTab && <Breadcrumbs />}

          {/* Editor Area */}
          <div className="ide-editor-area">
            {activeTab ? (
              <Editor
                file={activeTab}
                onFileChange={(content) => {
                  if (activeTab) actions.updateTabContent(activeTab.id, content);
                }}
              />
            ) : (
              <div className="ide-welcome">
                <div className="welcome-logo">
                  <motion.div
                    animate={{ rotate: [0, 360] }}
                    transition={{ duration: 34, repeat: Infinity, ease: 'linear' }}
                  >
                    <svg viewBox="0 0 100 100" width="80" height="80">
                      <defs>
                        <linearGradient id="wg" x1="0" y1="0" x2="1" y2="1">
                          <stop offset="0%" stopColor="#0EA5E9" />
                          <stop offset="100%" stopColor="#8B5CF6" />
                        </linearGradient>
                      </defs>
                      <polygon points="50,8 88,28 88,72 50,92 12,72 12,28" fill="none" stroke="url(#wg)" strokeWidth="3" />
                      <circle cx="50" cy="50" r="10" fill="url(#wg)" />
                    </svg>
                  </motion.div>
                </div>
                <h1 className="welcome-title">HeadyAI-IDE</h1>
                <p className="welcome-subtitle">Cloud-First AI Development Environment</p>
                <div className="welcome-shortcuts">
                  <div className="shortcut-item"><kbd>Ctrl+Shift+P</kbd><span>Command Palette</span></div>
                  <div className="shortcut-item"><kbd>Ctrl+Shift+E</kbd><span>Explorer</span></div>
                  <div className="shortcut-item"><kbd>Ctrl+Shift+F</kbd><span>Search</span></div>
                  <div className="shortcut-item"><kbd>Ctrl+Shift+I</kbd><span>AI Chat</span></div>
                  <div className="shortcut-item"><kbd>Ctrl+`</kbd><span>Terminal</span></div>
                </div>
              </div>
            )}
          </div>

          {/* Bottom Panel (Terminal/Output/Problems) */}
          <AnimatePresence>
            {bottomPanelOpen && (
              <motion.div
                className="ide-bottom-panel"
                initial={{ height: 0 }}
                animate={{ height: bottomPanelHeight }}
                exit={{ height: 0 }}
                transition={{ duration: 0.2 }}
              >
                <div className="bottom-panel-tabs">
                  {[
                    { id: PANELS.TERMINAL, label: 'Terminal' },
                    { id: PANELS.OUTPUT, label: 'Output' },
                    { id: PANELS.PROBLEMS, label: `Problems (${problems.length})` },
                    { id: PANELS.DEBUG_CONSOLE, label: 'Debug Console' },
                  ].map(panel => (
                    <button
                      key={panel.id}
                      className={`bottom-tab ${activeBottomPanel === panel.id ? 'active' : ''}`}
                      onClick={() => actions.setActiveBottomPanel(panel.id)}
                    >
                      {panel.label}
                    </button>
                  ))}
                  <div className="bottom-panel-spacer" />
                  <button className="bottom-panel-close" onClick={() => actions.toggleBottomPanel()}>✕</button>
                </div>
                <div className="bottom-panel-content">
                  {activeBottomPanel === PANELS.TERMINAL && (
                    <Terminal onCommand={handleTerminalCommand} />
                  )}
                  {activeBottomPanel === PANELS.OUTPUT && (
                    <div className="output-content">
                      {outputLines.length === 0
                        ? <div className="output-empty">No output</div>
                        : outputLines.map((line, i) => <div key={i} className="output-line">{line}</div>)
                      }
                    </div>
                  )}
                  {activeBottomPanel === PANELS.PROBLEMS && (
                    <div className="problems-content">
                      {problems.length === 0
                        ? <div className="problems-empty">No problems detected</div>
                        : problems.map((p, i) => (
                          <div key={i} className={`problem-item ${p.severity}`}>
                            <span className="problem-icon">{p.severity === 'error' ? '✗' : '⚠'}</span>
                            <span className="problem-msg">{p.message}</span>
                            <span className="problem-file">{p.file}:{p.line}</span>
                          </div>
                        ))
                      }
                    </div>
                  )}
                  {activeBottomPanel === PANELS.DEBUG_CONSOLE && (
                    <div className="debug-console-content">
                      <div className="debug-console-empty">Debug console ready</div>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* AI Chat Panel (right side) */}
        <AnimatePresence>
          {aiChatOpen && (
            <motion.div
              className="ide-ai-panel"
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 360, opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <AIChat
                model={selectedModel}
                onClose={() => actions.toggleAIChat()}
                activeFile={activeTab}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Status Bar */}
      <div className="ide-status-bar">
        <div className="status-left">
          <span className={`status-connection ${connectionStatus}`}>
            {connectionStatus === 'connected' ? '⬤' : '◯'} Cloud
          </span>
          <span className="status-branch">⎇ {gitBranch}</span>
        </div>
        <div className="status-center">
          <span className="status-info">HeadyAI-IDE v1.0.0 • Liquid Lattice Omega</span>
        </div>
        <div className="status-right">
          {activeTab && <span className="status-lang">{activeTab.name?.split('.').pop()?.toUpperCase() || 'TEXT'}</span>}
          <span className="status-encoding">UTF-8</span>
          <span className="status-model">🧠 {selectedModel}</span>
        </div>
      </div>
    </div>
  );
};

const App = () => {
  return (
    <IDEProvider>
      <IDELayout />
    </IDEProvider>
  );
};

export default App;
